import { durationLabel, parseWall, wallDayLabel, wallTime } from 'utils/shortTerm'
import React, { useEffect, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import { displayPrice } from 'utils/string'
import './shortTerm.scss'

/**
 * Szczegóły wynajmu krótkoterminowego (rentedreserved, kontrakt dla frontu §3) — zamiast bloczków dat
 * (dzień/miesiąc/rok bez godzin) i miesięcznego harmonogramu.
 */

const useNow = (intervalMs = 30000) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(interval)
  }, [intervalMs])
  return now
}

const toMs = (iso?: string) => {
  const time = iso ? new Date(iso).getTime() : NaN
  return Number.isNaN(time) ? undefined : time
}

export const VehicleSection: React.FC<{ warehouse: warehouse.Warehouse }> = ({ warehouse }) => {
  const { t } = useTranslation('warehouse.shortTerm')
  const photo = warehouse.vehicle?.photo || warehouse.warehouseTransparentPhoto || warehouse.warehousePhoto
  const registration = warehouse.vehicle?.registrationNumber

  return (
    <div>
      <div className='list__element --big shortTermDetails__vehicle'>
        {photo && <img
          src={photo}
          alt={warehouse.name}
          className='shortTermDetails__vehicle__photo' />}
        <div>
          <div className='element__title'>{warehouse.name}</div>
          {registration && <div className='element__desc'>
            <div>{t('registration')}: <b>{registration}</b></div>
          </div>}
        </div>
      </div>
    </div>
  )
}

export const RentalTimeSection: React.FC<{ warehouse: warehouse.Warehouse }> = ({ warehouse }) => {
  const { fullDateFrom, fullDateTo, startsAt, endsAt, durationMinutes, durationLabel: label } = warehouse
  const { t, lang } = useTranslation('warehouse.shortTerm')
  const now = useNow()

  const start = parseWall(fullDateFrom)
  const end = parseWall(fullDateTo)
  const startMs = toMs(startsAt)
  const endMs = toMs(endsAt)

  const minutesTo = (ms: number) => Math.max(1, Math.ceil((ms - now) / 60000))
  const fill = (key: string, minutes: number) => t(key).replace('{time}', durationLabel(minutes, lang))

  const status = (() => {
    if (warehouse.resignation?.isCompleted) return { modifier: 'finished', label: t(warehouse.resignation.type === 'cancel' ? 'canceled' : 'finished') }
    if (startMs === undefined || endMs === undefined) return
    if (now < startMs) return { modifier: 'upcoming', label: fill('upcoming', minutesTo(startMs)) }
    if (now < endMs) return { modifier: 'active', label: `${t('active')} · ${fill('timeLeft', minutesTo(endMs))}` }
    // po końcu najmu automat dopełnia umowę w ciągu minuty — do tego czasu umowa jeszcze jest na liście
    return { modifier: 'overdue', label: `${t('overdue')} ${fill('overdueBy', Math.max(1, Math.floor((now - endMs) / 60000)))}` }
  })()

  const progress = !warehouse.resignation?.isCompleted && startMs !== undefined && endMs !== undefined && endMs > startMs && now >= startMs && now < endMs
    ? (now - startMs) / (endMs - startMs)
    : undefined

  const fullDate = (m: number) => `${wallDayLabel(m, lang, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}, ${wallTime(m)}`

  return (
    <div>
      <div className='list__header element__title'>{t('rentalTime')}</div>
      <div className='shortTermDetails__time'>
        {status && <div className={`shortTermDetails__status --${status.modifier}`}>
          <span className='shortTermDetails__status__dot' />
          <span>{status.label}</span>
        </div>}

        {progress !== undefined && <div className='shortTermDetails__progress'>
          <div style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>}

        <div className='shortTermDetails__rows'>
          <div className='shortTermDetails__row'>
            <span>{t('pickup')}</span>
            <b>{start !== undefined ? fullDate(start) : '-'}</b>
          </div>
          <div className='shortTermDetails__row'>
            <span>{t('return')}</span>
            <b>{end !== undefined ? fullDate(end) : '∞'}</b>
          </div>
          <div className='shortTermDetails__row'>
            <span>{t('duration')}</span>
            <b>{label || (durationMinutes ? durationLabel(durationMinutes, lang) : '-')}</b>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ShortTermPaymentSection: React.FC<{ warehouse: warehouse.Warehouse, onPay: basic.BasicFunc }> = ({ warehouse, onPay }) => {
  const { t } = useTranslation('warehouse.shortTerm')
  const { t: tWarehouse } = useTranslation('warehouse')
  const items = (warehouse.schedule_list_details || []).flatMap(month => (month.list || []) as { text: string, price: number | string }[])

  return (
    <div>
      <div className='group'>
        <div className='list__header element__title'>{t('payment')}</div>
        {warehouse.needPayment && <div
          className='menu__more --secFont'
          onClick={onPay}>{tWarehouse('pay2')} →</div>}
      </div>
      <div className='list__element --big shortTermDetails__payment'>
        {items.map(({ text, price }, idx) => <div
          key={idx}
          className='shortTermDetails__row'>
          <span>{text}</span>
          <b>{displayPrice(price)}</b>
        </div>)}
        <div className={`shortTermDetails__payment__status --${warehouse.needPayment ? 'toPay' : 'paid'}`}>
          {t(warehouse.needPayment ? 'toPay' : 'paidInAdvance')}
        </div>
      </div>
    </div>
  )
}
